import { ScrollArea } from '@/components/ui/scroll-area/scroll-area.component';
import { useDimensions } from '@/hooks/use-dimensions.hook';
import { cn } from '@/lib/utils';
import { useMemo, useRef } from 'react';
import { RootLayout } from '../_root/root.layout';
import { ActionBar } from './components/action-bar.component';
import { NavigationBar } from './components/navigation-bar.component';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  context: 'products' | 'templates' | 'product-groups';

  loading: boolean;
  loadingComponent: React.ReactNode;

  noResults: boolean;
  noResultsComponent: React.ReactNode;

  children: React.ReactNode;
  navigationBarConfig: {
    productsTitle?: React.ReactNode;
    templatesTitle?: React.ReactNode;
    addButtonTitle?: React.ReactNode;
    addButtonAction: () => void;
    onNavClick?: () => void;
  };
  actionBarConfig: {
    searchPlaceholder: string;
    filterBarText: React.ReactNode;
    filterBarOptionAllProducts: React.ReactNode;
    filterBarOptionMyProducts: React.ReactNode;
    tableViewButton: React.ReactNode;
    gridViewButton: React.ReactNode;

    handleViewChange: () => void;
    selectedView: 'grid' | 'table';

    filter: string;
    handleFilterChange: (filter: string) => void;

    handleSearchChange: (search: string) => void;
  };
}

export const GridTableLayout = ({
  context,
  loading,
  loadingComponent,
  noResults,
  noResultsComponent,
  children,
  actionBarConfig,
  navigationBarConfig,
}: Props) => {
  const { height: windowHeight } = useDimensions();

  const HEADER_HEIGHT = 64;
  const FOOTER_HEIGHT = 40;

  const barsRef = useRef<HTMLDivElement>(null);
  const barsHeight = barsRef.current?.clientHeight || 0;

  const height = useMemo(() => {
    return windowHeight - barsHeight - HEADER_HEIGHT - FOOTER_HEIGHT - 24;
  }, [windowHeight, barsHeight]);

  return (
    <RootLayout>
      <div ref={barsRef} className="fixed top-[64px] left-0 right-0 bg-white pt-8 px-18">
        <NavigationBar
          context={context}
          productsTitle={navigationBarConfig.productsTitle}
          templatesTitle={navigationBarConfig.templatesTitle}
          addButtonTitle={navigationBarConfig.addButtonTitle}
          addButtonAction={navigationBarConfig.addButtonAction}
          handleNavigation={navigationBarConfig.onNavClick}
        />
        <ActionBar
          context={context}
          searchPlaceholder={actionBarConfig.searchPlaceholder}
          filterBarText={actionBarConfig.filterBarText}
          filterBarOptionAllProducts={actionBarConfig.filterBarOptionAllProducts}
          filterBarOptionMyProducts={actionBarConfig.filterBarOptionMyProducts}
          tableViewButton={actionBarConfig.tableViewButton}
          gridViewButton={actionBarConfig.gridViewButton}
          selectedView={actionBarConfig.selectedView}
          onHandleViewChange={actionBarConfig.handleViewChange}
          filter={actionBarConfig.filter}
          onHandleFilterChange={actionBarConfig.handleFilterChange}
          onHandleSearchChange={actionBarConfig.handleSearchChange}
        />
      </div>
      <div className={cn('mx-18 mb-[60px] xs:mx-6 xs:my-4 mt-[218px]')}>
        {noResults && noResultsComponent}
        {loading && actionBarConfig.selectedView === 'grid' && loadingComponent}
        {loading && actionBarConfig.selectedView === 'table' && loadingComponent}
        {actionBarConfig.selectedView === 'grid' && !loading && !noResults && (
          <ScrollArea
            style={{
              overflowY: 'auto',
              maxHeight: height,
              height,
            }}
          >
            <div className="grid grid-cols-4 gap-3 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {children}
            </div>
          </ScrollArea>
        )}
        {actionBarConfig.selectedView === 'table' && !noResults && !loading && (
          <ScrollArea
            style={{
              overflowY: 'auto',
              maxHeight: height,
              height,
            }}
          >
            {children}
          </ScrollArea>
        )}
      </div>
    </RootLayout>
  );
};
